import {useQueries, type UseQueryResult} from "@tanstack/react-query";
import {useCallback} from "react";
import {sendHookRequest, type BackendAPIBlueprint, type BackendAPITypes} from "../../../../../generated/blueprint.ts";
import {QueryKey} from "../../../../../api/queryKeys.ts";
import type {CategorizationChange} from "../../../entrepreneur/pages/TransactionsPage/TransactionsTable.tsx";

type CategorizationChangeResponse =
    BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerTransactionsCategorizationChangePostResponseBody;

interface PageWithTransactionIds {
    items: ReadonlyArray<{transaction_id: string}>;
}

interface TransactionPageEnrichment {
    categorizationChangeById: Record<string, CategorizationChange>;
    pendingCategorizationChangeIds: Set<string>;
    resolvedTransactionIds: Set<string>;
}

interface PageEnrichmentQueryState {
    transactionIds: string[];
    isPending: boolean;
    data: CategorizationChangeResponse | undefined;
}

function combineEnrichment(results: PageEnrichmentQueryState[]): TransactionPageEnrichment {
    const categorizationChangeById: Record<string, CategorizationChange> = {};
    const pendingCategorizationChangeIds = new Set<string>();
    const resolvedTransactionIds = new Set<string>();

    for (const result of results) {
        if (result.isPending) {
            result.transactionIds.forEach(transactionId => pendingCategorizationChangeIds.add(transactionId));
            continue;
        }
        result.transactionIds.forEach(transactionId => resolvedTransactionIds.add(transactionId));
        result.data?.forEach(change => {
            categorizationChangeById[change.transaction_id] = change;
        });
    }

    return {categorizationChangeById, pendingCategorizationChangeIds, resolvedTransactionIds};
}

export function usePartnerTransactionPageEnrichment(
    api: BackendAPIBlueprint,
    pages: ReadonlyArray<PageWithTransactionIds>,
): TransactionPageEnrichment {
    const combine = useCallback(
        (results: UseQueryResult<CategorizationChangeResponse>[]) => combineEnrichment(results.map((result, index) => ({
            transactionIds: pages[index].items.map(item => item.transaction_id),
            isPending: result.isPending,
            data: result.data,
        }))),
        [pages],
    );

    return useQueries({
        queries: pages.map(page => {
            const transactionIds = page.items.map(item => item.transaction_id);
            return {
                queryKey: [QueryKey.PartnerTransactions, 'partner/transactions/categorization-change', {transaction_ids: transactionIds}],
                queryFn: ({signal}: {signal: AbortSignal}) => sendHookRequest<CategorizationChangeResponse>(
                    api,
                    'post',
                    'partner/transactions/categorization-change',
                    {transaction_ids: transactionIds},
                    signal,
                ),
                enabled: transactionIds.length > 0,
                refetchOnWindowFocus: false,
            };
        }),
        combine,
    });
}
